import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import { useEffect, useState, type ChangeEvent } from 'react';
import { useStyles } from '../../../../styles';
import { useParams } from 'react-router';

type PopupType = 'addSLA' | 'editSLA' | 'addTransaction' | 'editTransaction';

type SlaData = {
  slaId?: string;
  slaIds?: string[];
  averageUserLoad?: string | number;
  peakUserLoad?: string | number;
  availability?: string;
  transactionName?: string;
  expectedResponseTime?: string;
  locationPercentages?: Record<string, string>;
  userBaseLocation?: string[];
  user_base_location?: string[];
  availableUserBaseLocations?: string[];
};

type FormData = {
  apmNumber: string;
  averageUserLoad: string | number;
  peakUserLoad: string | number;
  availability: string;
  transactionName: string;
  expectedResponseTime: string;
};

type ErrorState = Record<string, string>;

type SubmitSlaData = {
  apmNumber: string;
  averageUserLoad: number;
  peakUserLoad: number;
  availability: string;
  userBaseLocation: string[];
  locationPercentages: Record<string, string>;
};

type SubmitTransactionData = {
  transactionName: string;
  expectedResponseTime: string;
  slaId?: string;
};

type SLAPopupProps = {
  open: boolean;
  title: string;
  type: PopupType;
  onClose: () => void;
  onConfirm: (data: SubmitSlaData | SubmitTransactionData, selectedSlaId?: string) => void;
  data?: SlaData | string[];
};

const SLAPopup = ({ open, title, type, onClose, onConfirm, data }: SLAPopupProps) => {
  const classes = useStyles();
  const popupData = Array.isArray(data) ? undefined : data;

  useEffect(() => {
    if (popupData?.locationPercentages) {
      setLocationPercentages(popupData.locationPercentages);
    }
  }, [popupData]);

  const { projectId } = useParams();
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    popupData?.locationPercentages
      ? Object.keys(popupData.locationPercentages)
      : popupData?.userBaseLocation || popupData?.user_base_location || [],
  );
  const [selectedSlaId, setSelectedSlaId] = useState(popupData?.slaId || '');
  const handleSlaIdChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = String(event.target.value || '');
    setSelectedSlaId(value);
    if (value) {
      setErrors(prev => ({ ...prev, selectedSlaId: '' }));
    }
  };

  const [errors, setErrors] = useState<ErrorState>({});
  const [locationPercentages, setLocationPercentages] = useState<Record<string, string>>(
    popupData?.locationPercentages || {},
  );

  const [formData, setFormData] = useState<FormData>({
    apmNumber: projectId!,
    averageUserLoad: popupData?.averageUserLoad ? popupData.averageUserLoad : '',
    peakUserLoad: popupData?.peakUserLoad ? popupData.peakUserLoad : '',
    availability: popupData?.availability ? popupData.availability : '',
    transactionName: popupData?.transactionName ? popupData.transactionName : '',
    expectedResponseTime: popupData?.expectedResponseTime
      ? popupData.expectedResponseTime
      : '',
  });

  const handleLocationChange = (event: ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value;
    const newSelectedLocations =
      typeof value === 'string' ? value.split(',') : (value as string[]);
    setSelectedLocations(newSelectedLocations);
    // Add/remove percentage fields based on selection
    const newPercentages = { ...locationPercentages };

    // Remove percentages for unselected locations
    Object.keys(newPercentages).forEach(location => {
      if (!newSelectedLocations.includes(location)) {
        delete newPercentages[location];
      }
    });

    // Add empty percentages for newly selected locations
    newSelectedLocations.forEach(location => {
      if (!newPercentages[location]) {
        newPercentages[location] = '';
      }
    });

    setLocationPercentages(newPercentages);

    if (newSelectedLocations.length > 0) {
      setErrors(prev => ({
        ...prev,
        selectedLocations: '',
        percentageSum: '',
      }));
    }
  };

  const handleLocationPercentageChange = (location: string, value: string) => {
    setLocationPercentages(prev => ({
      ...prev,
      [location]: value,
    }));

    // Clear percentage sum error when user changes values
    setErrors(prev => ({
      ...prev,
      percentageSum: '',
      [`percentage_${location}`]: '',
    }));
  };

  const validateLocationPercentages = () => {
    const newErrors: ErrorState = {};
    let totalPercentage = 0;
    const numberOfLocations = selectedLocations?.length;

    // Check if all location percentages are filled
    selectedLocations.forEach((location: string) => {
      const percentage = locationPercentages[location];
      if (!percentage || percentage.trim() === '') {
        newErrors[
          `percentage_${location}`
        ] = `${location} percentage is required`;
      } else {
        const numValue = Number(percentage);
        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
          newErrors[
            `percentage_${location}`
          ] = `${location} percentage must be between 0 and 100`;
        } else {
          totalPercentage += numValue;
        }
      }
    });

    // Check if total percentage equals exactly 100% based on number of locations
    if (totalPercentage !== 100 && numberOfLocations > 0) {
      newErrors.percentageSum = `For ${numberOfLocations} locations, total percentage must be exactly 100% (currently ${totalPercentage}%)`;
    }

    return { errors: newErrors, totalPercentage };
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (value.trim()) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: ErrorState = {};

    if (type === 'addSLA' || type === 'editSLA') {
      // SLA validation
      if (!formData?.averageUserLoad) {
        newErrors.averageUserLoad = 'Average user load is required';
      }
      if (!formData?.peakUserLoad) {
        newErrors.peakUserLoad = 'Peak user load is required';
      }
      if (!formData?.availability?.trim()) {
        newErrors.availability = 'Availability is required';
      }
      if (selectedLocations?.length === 0) {
        newErrors.selectedLocations = 'User base location is required';
      } else {
        // Validate location percentages
        const percentageValidation = validateLocationPercentages();
        Object.assign(newErrors, percentageValidation.errors);
      }
    } else {
      // Transaction validation
      if (!selectedSlaId) {
        newErrors.selectedSlaId = 'SLA selection is required';
      }
      if (!formData?.transactionName?.trim()) {
        newErrors.transactionName = 'Transaction name is required';
      }
      if (!formData?.expectedResponseTime?.trim()) {
        newErrors.expectedResponseTime = 'Expected response time is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    if (type === 'addSLA' || type === 'editSLA') {
      const submitData = {
        apmNumber: formData?.apmNumber,
        averageUserLoad: Number(formData?.averageUserLoad) || 0,
        peakUserLoad: Number(formData?.peakUserLoad) || 0,
        availability: formData?.availability,
        userBaseLocation: selectedLocations,
        locationPercentages: locationPercentages,
      };
      onConfirm(submitData, popupData?.slaId);
    } else {
      const submitData = {
        transactionName: formData?.transactionName,
        expectedResponseTime: formData?.expectedResponseTime,
      } as SubmitTransactionData;
      if (type === 'editTransaction') {
        submitData.slaId = popupData?.slaId;
      }
      onConfirm(submitData, selectedSlaId);
    }
  };

  const getTotalPercentage = () => {
    return Object.values(locationPercentages).reduce((sum: number, value) => {
      const num = Number(value);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <div className={classes.popupTitle}>
        <DialogTitle>
          <span>{title}</span>
        </DialogTitle>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      <DialogContent
        className={classes.dialogContent}
        classes={{ root: classes.dialogFieldFlow }}
      >
        {type === 'addSLA' || type === 'editSLA' ? (
          <>
            <TextField
              required
              name="averageUserLoad"
              value={formData.averageUserLoad}
              onChange={handleInputChange}
              placeholder="Enter average user load"
              label="Average user load"
              variant="outlined"
              fullWidth
              error={!!errors?.averageUserLoad}
              helperText={errors?.averageUserLoad}
            />
            <TextField
              required
              name="peakUserLoad"
              value={formData.peakUserLoad}
              onChange={handleInputChange}
              placeholder="Enter peak user load"
              label="Peak user load"
              variant="outlined"
              fullWidth
              error={!!errors?.peakUserLoad}
              helperText={errors?.peakUserLoad}
            />
            <TextField
              required
              name="availability"
              value={formData.availability}
              onChange={handleInputChange}
              placeholder="Enter availability"
              label="Availability"
              variant="outlined"
              fullWidth
              error={!!errors?.availability}
              helperText={errors?.availability}
            />
            <FormControl
              variant="outlined"
              fullWidth
              required
              error={!!errors?.selectedLocations}
            >
              <InputLabel>User base location</InputLabel>
              <Select
                multiple
                value={selectedLocations}
                onChange={handleLocationChange}
                label="User base location"
                renderValue={selected => (selected as string[]).join(', ')}
                startAdornment={
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                }
              >
                {Array.isArray(data)
                  ? data.map((location: string) => (
                      <MenuItem key={location} value={location}>
                        <Checkbox
                          checked={selectedLocations.indexOf(location) > -1}
                        />
                        <ListItemText primary={location} />
                      </MenuItem>
                    ))
                  : data?.availableUserBaseLocations?.map(
                      (location: string) => (
                        <MenuItem key={location} value={location}>
                          <Checkbox
                            checked={selectedLocations.indexOf(location) > -1}
                          />
                          <ListItemText primary={location} />
                        </MenuItem>
                      ),
                    )}
              </Select>
              {errors?.selectedLocations && (
                <FormHelperText>{errors?.selectedLocations}</FormHelperText>
              )}
            </FormControl>
            {/* Location Percentage Fields */}
            {selectedLocations.length > 0 && (
              <>
                <Grid container spacing={2}>
                  {selectedLocations.map(location => (
                    <Grid
                      item
                      xs={12}
                      sm={selectedLocations?.length === 1 ? 12 : 6}
                      key={location}
                    >
                      <TextField
                        required
                        type="number"
                        value={locationPercentages[location] || ''}
                        onChange={e =>
                          handleLocationPercentageChange(
                            location,
                            e.target.value,
                          )
                        }
                        placeholder="Enter percentage"
                        label={`${location} Percentage`}
                        variant="outlined"
                        fullWidth
                        inputProps={{ min: 0, max: 100 }}
                        error={!!errors?.[`percentage_${location}`]}
                        helperText={errors?.[`percentage_${location}`]}
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* Total Percentage Display */}
                <div>
                  <Typography
                    variant="body2"
                    color={getTotalPercentage() !== 100 ? 'error' : 'primary'}
                  >
                    Total: {getTotalPercentage()}% / 100%
                    {selectedLocations.length > 0 && (
                      <span style={{ fontWeight: 'bold', marginLeft: '8px' }}>
                        (Must equal exactly 100%)
                      </span>
                    )}
                  </Typography>
                  {errors?.percentageSum && (
                    <FormHelperText error>
                      {errors.percentageSum}
                    </FormHelperText>
                  )}
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <FormControl
              variant="outlined"
              fullWidth
              required
              error={!!errors?.selectedSlaId}
            >
              <InputLabel>Select SLA</InputLabel>
              <Select
                name="selectedSlaId"
                value={selectedSlaId}
                onChange={handleSlaIdChange}
                label="Select SLA"
              >
                {Array.isArray(data)
                  ? data.map((slaId: string) => (
                      <MenuItem key={slaId} value={slaId}>
                        {slaId}
                      </MenuItem>
                    ))
                  : data?.slaIds?.map((slaId: string) => (
                      <MenuItem key={slaId} value={slaId}>
                        {slaId}
                      </MenuItem>
                    ))}
              </Select>
              {errors?.selectedSlaId && (
                <FormHelperText>{errors?.selectedSlaId}</FormHelperText>
              )}
            </FormControl>
            <TextField
              required
              name="transactionName"
              value={formData.transactionName}
              onChange={handleInputChange}
              placeholder="Enter transaction name"
              label="Transaction"
              variant="outlined"
              fullWidth
              error={!!errors?.transactionName}
              helperText={errors?.transactionName}
            />
            <Typography
              variant="caption"
              style={{
                color: '#1976d2',
                display: 'block',
                marginTop: '-12px',
              }}
            >
              Note: The Transaction Name should match the Script Transaction
              Name
            </Typography>
            <TextField
              required
              name="expectedResponseTime"
              value={formData.expectedResponseTime}
              onChange={handleInputChange}
              placeholder="Enter response time"
              label="Expected response time"
              variant="outlined"
              fullWidth
              error={!!errors?.expectedResponseTime}
              helperText={errors?.expectedResponseTime}
            />
          </>
        )}
      </DialogContent>
      <DialogActions classes={{ root: classes.pagePadding }}>
        <Button onClick={onClose} color="primary" variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {type === 'addTransaction' ? 'Add' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SLAPopup;
